import React from "react";
import { Link } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import { Target, Star, Zap, DollarSign, Trophy, Users, Phone } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const perks = [
    { icon: DollarSign, title: "14-Point Payout", desc: "For new ISO partners on qualified files. More points. More rewards. More growth." },
    { icon: Zap, title: "Same-Day Decisions", desc: "Complete files get a yes-or-no in under 4 hours. No 'pending review' for a week." },
    { icon: Users, title: "Direct Underwriter Access", desc: "Your underwriter's direct line. Real human, real authority, real answers." },
    { icon: Trophy, title: "Stack-Friendly Capital", desc: "2nd, 3rd, 4th… up to 8th positions. We understand position math." },
    { icon: Star, title: "Renewal Bonuses", desc: "Aggressive APD schedule means faster renewals — and faster commissions." },
    { icon: Target, title: "$3M Deal Capacity", desc: "Built for high-dollar deals. Bring your serious files. We sharpen the pencil." },
];

export default function ISOPartners() {
    usePageTitle("ISO Partners — 14-Point Payout", "Earn 14 points. Close more, earn more, faster. Direct underwriter access, 24-hour funding, deals up to $3M for new ISO partners.");
    return (
        <>
            <section className="bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="iso-hero">
                <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
                <div className="absolute -top-32 -left-20 w-[600px] h-[600px] bg-[#FF6B1A]/15 blur-3xl rounded-full pointer-events-none" />
                <div className="container-lended relative pt-20 pb-24 md:pt-28 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-1 w-16 bg-[#FF6B1A]" />
                            <span className="eyebrow text-[#FF6B1A]">For ISO Partners</span>
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.04em] leading-[0.95]">
                            Earn <span className="text-[#FF6B1A]">14 points.</span> <br />
                            Close more. Earn more. Faster.
                        </h1>
                        <p className="mt-7 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                            New ISO partners get our most aggressive payout structure on qualifying files. Real underwriters,
                            real closings, real commissions paid the next business day.
                        </p>
                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 max-w-2xl">
                            {[
                                ["14", "Points"],
                                ["24h", "To Fund"],
                                ["$3M", "Deal Cap"],
                                ["1.2K+", "ISOs"],
                            ].map(([v, l]) => (
                                <div key={l} className="bg-[#0A0A0A] p-5 text-center">
                                    <div className="stat-num text-3xl md:text-4xl text-[#FF6B1A]">{v}</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] mt-2 text-white/55">{l}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <a href="#iso-apply" className="btn-orange" data-testid="iso-apply-cta">Become a Partner</a>
                            <Link to="/contact" className="btn-outline-black border-white text-white hover:bg-white hover:text-black">
                                Contact Us
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5" id="iso-apply">
                        <LeadForm defaultAudience="iso" simple />
                    </div>
                </div>
            </section>

            <section className="bg-white py-20 md:py-28" data-testid="iso-perks">
                <div className="container-lended">
                    <div className="max-w-2xl mb-14">
                        <span className="eyebrow">Why Top ISOs Move Volume Through Us</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1]">
                            Six reasons your next file should land here.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
                        {perks.map((p) => (
                            <div key={p.title} className="bg-white p-8 hover:bg-[#0A0A0A] hover:text-white group transition-colors" data-testid={`iso-perk-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                <p.icon className="text-[#FF6B1A]" size={28} />
                                <div className="font-display font-bold text-xl mt-5">{p.title}</div>
                                <p className="mt-2 text-sm text-black/60 group-hover:text-white/65 leading-relaxed">{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#F8F5F2] py-20" data-testid="iso-commission">
                <div className="container-lended">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-6">
                            <span className="eyebrow">Commission Schedule</span>
                            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-[1]">
                                Transparent, tiered, paid the next business day.
                            </h2>
                            <p className="text-black/70 mt-5 text-lg max-w-xl">
                                We don't bury our payout schedule in a 60-page agreement. Here's exactly what you earn.
                            </p>
                        </div>
                        <div className="lg:col-span-6">
                            <div className="border-2 border-[#0A0A0A] bg-white">
                                <div className="grid grid-cols-3 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.18em] py-4 px-5">
                                    <div>Tier</div>
                                    <div className="text-center">Volume / Mo</div>
                                    <div className="text-right">Commission</div>
                                </div>
                                {[
                                    ["Standard", "$0 – $250K", "Up to 10 pts"],
                                    ["Preferred", "$250K – $1M", "Up to 12 pts"],
                                    ["Elite", "$1M – $3M", "Up to 14 pts"],
                                    ["Diamond", "$3M+", "Custom"],
                                ].map((row) => (
                                    <div key={row[0]} className="grid grid-cols-3 py-5 px-5 border-t border-black/10 items-center">
                                        <div className="font-display font-bold">{row[0]}</div>
                                        <div className="text-center text-black/70">{row[1]}</div>
                                        <div className="text-right font-display font-bold text-[#FF6B1A]">{row[2]}</div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-black/50 mt-3">Subject to underwriting and qualifying file mix. Reach out for full schedule.</p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
